import React from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { FiClock, FiTrash2, FiMapPin } from 'react-icons/fi';
import { GuardData } from '../service/guardService';
import { deleteGuardDeploymentHistory } from '../../deployment/service/deploymentService';
import toast from 'react-hot-toast';

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  max-height: 60vh;
  overflow-y: auto;
`;

const HistoryItem = styled.div`
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.light.border.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HistoryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ClientName = styled.div`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const DateRange = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const EmptyHistory = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.light.text.muted};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

interface DeploymentHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    guard: GuardData | null;
    companyId: string;
    onUpdate: () => void;
}

const DeploymentHistoryModal: React.FC<DeploymentHistoryModalProps> = ({
    isOpen,
    onClose,
    guard,
    companyId,
    onUpdate
}) => {
    if (!guard) return null;

    const history = guard.history || [];

    const handleDeleteHistory = async (deployment: any) => {
        if (!window.confirm('Are you sure you want to delete this history record?')) return;

        try {
            await deleteGuardDeploymentHistory(guard.id!, deployment, companyId);
            toast.success('History record deleted');
            onUpdate();
        } catch (error: any) {
            toast.error(error.message || 'Failed to delete history record');
        }
    };

    const formatDate = (date: any) => {
        if (!date) return '—';
        const d = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
        return d.toLocaleDateString();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Deployment History: ${guard.name}`}
            footer={
                <Button variant="secondary" onClick={onClose} fullWidth dark>
                    Close
                </Button>
            }
        >
            <HistoryList>
                {history.length === 0 ? (
                    <EmptyHistory>
                        <FiClock size={32} />
                        <div>No deployment history found for this guard.</div>
                    </EmptyHistory>
                ) : (
                    [...history].reverse().map((item, index) => (
                        <HistoryItem key={index}>
                            <HistoryInfo>
                                <ClientName>
                                    <FiMapPin size={14} />
                                    {item.client?.name || 'Unknown Client'} ({item.client?.type || 'N/A'})
                                </ClientName>
                                <DateRange>
                                    <FiClock size={12} />
                                    {formatDate((item as any).postDate)} — {formatDate((item as any).endDate || (item as any).completedAt)}
                                </DateRange>
                                {((item as any).slotNumber) && (
                                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                                        Slot: {(item as any).slotNumber}
                                    </div>
                                )}
                            </HistoryInfo>
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteHistory(item)}
                                title="Delete history entry"
                                style={{ padding: theme.spacing[2], minWidth: 'auto' }}
                            >
                                <FiTrash2 size={16} />
                            </Button>
                        </HistoryItem>
                    ))
                )}
            </HistoryList>
        </Modal>
    );
};

export default DeploymentHistoryModal;
